import React, { useState, useEffect } from 'react';
import api from '../services/api';

function ShippingSettingsPage() {
  const [settings, setSettings] = useState({
    api_token: '',
    organization_id: '',
    sandbox_mode: true,
    default_sender_name: '',
    default_sender_phone: '',
    default_sender_email: '',
    default_sender_address: '',
    default_sender_city: '',
    default_sender_post_code: '',
    default_parcel_template: 'small',
    default_service: 'inpost_locker_standard',
    auto_print_label: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState(null);
  const [hasToken, setHasToken] = useState(false);
  const [tokenMasked, setTokenMasked] = useState('');
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/shipping/settings');
      const data = response.data;
      if (data.success) {
        setHasToken(data.data.has_api_token);
        setTokenMasked(data.data.api_token_masked || '');
        setSettings(prev => ({
          ...prev,
          ...data.data,
          api_token: '' // Nie pokazuj tokenu
        }));
      }
    } catch (error) {
      console.error('Błąd pobierania ustawień:', error);
      setMessage({ type: 'error', text: 'Błąd pobierania ustawień' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    
    try {
      const dataToSave = { ...settings };
      // Nie wysyłaj pustego tokenu jeśli już istnieje
      if (!dataToSave.api_token && hasToken) {
        delete dataToSave.api_token;
      }
      
      const response = await api.post('/shipping/settings', dataToSave);
      const result = response.data;

      if (result.success) {
        setMessage({ type: 'success', text: 'Ustawienia zapisane pomyślnie!' });
        if (settings.api_token) {
          setHasToken(true);
          setTokenMasked(settings.api_token.substring(0, 10) + '...' + settings.api_token.slice(-4));
          setSettings(prev => ({ ...prev, api_token: '' }));
        }
      } else {
        setMessage({ type: 'error', text: result.message || 'Błąd zapisywania' });
      }
    } catch (error) {
      console.error('Błąd zapisu:', error);
      setMessage({ type: 'error', text: 'Błąd zapisywania: ' + error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    
    try {
      const response = await api.post('/shipping/test-connection');
      const data = response.data;
      setTestResult({
        success: data.success,
        message: data.message,
        data: data.data
      });
    } catch (error) {
      setTestResult({
        success: false,
        message: error.message || 'Błąd połączenia'
      });
    } finally {
      setTesting(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: '1px solid var(--border)',
    borderRadius: '4px',
    fontSize: '13px'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '12px',
    fontWeight: '500',
    color: 'var(--text-secondary)',
    marginBottom: '4px'
  };

  const sectionStyle = {
    background: "var(--card-bg)",
    borderRadius: '8px',
    padding: '1.25rem',
    marginBottom: '1rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <span>⏳ Ładowanie ustawień...</span>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '1.5rem' }}>📦</span>
          Ustawienia Wysyłki - InPost
        </h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '13px', marginTop: '0.5rem' }}>
          Konfiguracja integracji z API InPost ShipX
        </p>
      </div>

      {/* Alert */}
      {message && (
        <div style={{
          padding: '0.75rem 1rem',
          borderRadius: '4px',
          marginBottom: '1rem',
          background: message.type === 'success' ? 'var(--success-light)' : 'var(--danger-light)',
          color: message.type === 'success' ? 'var(--success)' : 'var(--danger)',
          border: `1px solid ${message.type === 'success' ? 'var(--success-light)' : 'var(--danger-light)'}`
        }}>
          {message.type === 'success' ? '✅' : '❌'} {message.text}
        </div>
      )}

      {/* Sekcja API */}
      <div style={sectionStyle}>
        <h3 style={{ margin: '0 0 1rem 0', fontSize: '14px', color: 'var(--text-secondary)' }}>
          🔐 Autoryzacja API
        </h3>
        
        <div style={{ marginBottom: '1rem' }}>
          <label style={labelStyle}>Token API InPost *</label>
          <input
            type="text"
            value={settings.api_token}
            onChange={e => setSettings({ ...settings, api_token: e.target.value })}
            placeholder={hasToken ? `Obecny token: ${tokenMasked}` : 'Wprowadź token API'}
            style={inputStyle}
          />
          {hasToken && (
            <small style={{ color: 'var(--success)', fontSize: '11px' }}>
              ✅ Token zapisany: {tokenMasked}
            </small>
          )}
          <br />
          <small style={{ color: 'var(--text-muted)', fontSize: '11px' }}>
            Token możesz wygenerować w panelu InPost Manager
          </small>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={labelStyle}>ID Organizacji</label>
          <input
            type="text"
            value={settings.organization_id}
            onChange={e => setSettings({ ...settings, organization_id: e.target.value })}
            placeholder="np. 12345"
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={settings.sandbox_mode}
              onChange={e => setSettings({ ...settings, sandbox_mode: e.target.checked })}
              style={{ width: '16px', height: '16px' }}
            />
            <span>🧪 Tryb testowy (Sandbox)</span>
          </label>
          <small style={{ color: 'var(--text-muted)', fontSize: '11px', marginLeft: '24px' }}>
            Włącz aby testować bez wysyłania prawdziwych przesyłek
          </small>
        </div>

        <button
          onClick={handleTestConnection}
          disabled={testing || !hasToken}
          style={{
            padding: '0.5rem 1rem',
            background: testing ? 'var(--text-muted)' : 'var(--info)',
            color: '#fff' ,
            borderRadius: '4px',
            cursor: testing || !hasToken ? 'not-allowed' : 'pointer',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          {testing ? '⏳ Testowanie...' : '🔌 Testuj połączenie'}
        </button>

        {testResult && (
          <div style={{
            marginTop: '1rem',
            padding: '0.75rem',
            borderRadius: '4px',
            background: testResult.success ? 'var(--success-light)' : 'var(--danger-light)',
            color: testResult.success ? 'var(--success)' : 'var(--danger)'
          }}>
            <strong>{testResult.success ? '✅ Sukces' : '❌ Błąd'}</strong>
            <p style={{ margin: '0.5rem 0 0' }}>{testResult.message}</p>
            {testResult.data?.organizations && (
              <div style={{ marginTop: '0.5rem', fontSize: '12px' }}>
                <strong>Tryb:</strong> {testResult.data.mode === 'sandbox' ? '🧪 Sandbox' : '🚀 Produkcja'}
                <br />
                <strong>Organizacje:</strong> {testResult.data.organizations.length}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sekcja nadawcy */}
      <div style={sectionStyle}>
        <h3 style={{ margin: '0 0 1rem 0', fontSize: '14px', color: 'var(--text-secondary)' }}>
          📤 Domyślne dane nadawcy
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div>
            <label style={labelStyle}>Nazwa firmy / Nadawca</label>
            <input
              type="text"
              value={settings.default_sender_name}
              onChange={e => setSettings({ ...settings, default_sender_name: e.target.value })}
              placeholder="Nazwa Twojej firmy"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Telefon</label>
            <input
              type="tel"
              value={settings.default_sender_phone}
              onChange={e => setSettings({ ...settings, default_sender_phone: e.target.value })}
              placeholder="600 123 456"
              style={inputStyle}
            />
          </div>
        </div>

        <div style={{ marginTop: '0.75rem' }}>
          <label style={labelStyle}>Email</label>
          <input
            type="email"
            value={settings.default_sender_email}
            onChange={e => setSettings({ ...settings, default_sender_email: e.target.value })}
            placeholder="kontakt@firma.pl"
            style={inputStyle}
          />
        </div>

        <div style={{ marginTop: '0.75rem' }}>
          <label style={labelStyle}>Adres (ulica i numer)</label>
          <input
            type="text"
            value={settings.default_sender_address}
            onChange={e => setSettings({ ...settings, default_sender_address: e.target.value })}
            placeholder="ul. Przykładowa 1"
            style={inputStyle}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', marginTop: '0.75rem' }}>
          <div>
            <label style={labelStyle}>Miasto</label>
            <input
              type="text"
              value={settings.default_sender_city}
              onChange={e => setSettings({ ...settings, default_sender_city: e.target.value })}
              placeholder="Warszawa"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Kod pocztowy</label>
            <input
              type="text"
              value={settings.default_sender_post_code}
              onChange={e => setSettings({ ...settings, default_sender_post_code: e.target.value })}
              placeholder="00-000"
              style={inputStyle}
            />
          </div>
        </div>
      </div>

      {/* Sekcja domyślnych ustawień */}
      <div style={sectionStyle}>
        <h3 style={{ margin: '0 0 1rem 0', fontSize: '14px', color: 'var(--text-secondary)' }}>
          ⚙️ Domyślne ustawienia przesyłek
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div>
            <label style={labelStyle}>Domyślna usługa</label>
            <select
              value={settings.default_service}
              onChange={e => setSettings({ ...settings, default_service: e.target.value })}
              style={inputStyle}
            >
              <option value="inpost_locker_standard">📦 Paczkomat</option>
              <option value="inpost_courier_standard">🚚 Kurier InPost</option>
              <option value="inpost_courier_express_1000">⚡ Kurier Express 10:00</option>
              <option value="inpost_courier_express_1200">⚡ Kurier Express 12:00</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Domyślny rozmiar paczki</label>
            <select
              value={settings.default_parcel_template}
              onChange={e => setSettings({ ...settings, default_parcel_template: e.target.value })}
              style={inputStyle}
            >
              <option value="small">📦 Gabaryt A (mały) - 8x38x64 cm</option>
              <option value="medium">📦 Gabaryt B (średni) - 19x38x64 cm</option>
              <option value="large">📦 Gabaryt C (duży) - 41x38x64 cm</option>
            </select>
          </div>
        </div>

        <div style={{ marginTop: '1rem' }}>
          <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={settings.auto_print_label}
              onChange={e => setSettings({ ...settings, auto_print_label: e.target.checked })}
              style={{ width: '16px', height: '16px' }}
            />
            <span>🖨️ Automatycznie drukuj etykietę po utworzeniu przesyłki</span>
          </label>
        </div>
      </div>

      {/* Przycisk zapisu */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '0.75rem 1.5rem',
            background: saving ? 'var(--text-muted)' : 'var(--success)',
            color: '#fff' ,
            borderRadius: '4px',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontSize: '13px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          {saving ? '⏳ Zapisywanie...' : '💾 Zapisz ustawienia'}
        </button>
      </div>

      {/* Info */}
      <div style={{ 
        marginTop: '2rem', 
        padding: '1rem', 
        background: 'var(--info-light)', 
        borderRadius: '8px',
        border: '1px solid var(--info-light)'
      }}>
        <h4 style={{ margin: '0 0 0.5rem', fontSize: '13px', color: 'var(--info)' }}>
          ℹ️ Jak uzyskać token API InPost?
        </h4>
        <ol style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '12px', color: 'var(--text-secondary)' }}>
          <li>Zaloguj się do <a href="https://manager.paczkomaty.pl" target="_blank" rel="noopener noreferrer">InPost Manager</a></li>
          <li>Przejdź do Ustawienia → API</li>
          <li>Wygeneruj nowy token API</li>
          <li>Skopiuj token i wklej powyżej</li>
        </ol>
        <p style={{ margin: '0.5rem 0 0', fontSize: '11px', color: 'var(--text-muted)' }}>
          Dokumentacja API: <a href="https://dokumentacja-inpost.atlassian.net/wiki/spaces/PL/pages/11731040" target="_blank" rel="noopener noreferrer">ShipX API</a>
        </p>
      </div>
    </div>
  );
}

export default ShippingSettingsPage;
